import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PersonContactDownloadButton } from "@/components/people/PersonContactDownloadButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryResult } from "@/components/QueryResult";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PersonForm } from "@/components/forms/PersonForm";
import { SkillMatrix } from "@/components/person-detail/SkillMatrix";
import { PersonBookings } from "@/components/person-detail/PersonBookings";
import {
  usePerson,
  useCreatePerson,
  useUpdatePerson,
  type PersonInsert,
  type PersonUpdate,
  PersonType,
} from "@/hooks/usePeople";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { useAuth } from "@/hooks/useAuth";
import { EmploymentPeriodForm } from "@/components/hr/EmploymentPeriodForm";
import { EntitlementCard } from "@/components/hr/EntitlementCard";
import { AbsencesOvertimePanel } from "@/components/hr/AbsencesOvertimePanel";

const VALID_TABS = [
  "details",
  "bookings",
  "skills",
  "employment",
  "absences-overtime",
] as const;
type TabValue = (typeof VALID_TABS)[number];

export default function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEmployee } = useAuth();
  const isNew = !id || id === "new";

  // Determine default type from route
  const defaultType: PersonType = location.pathname.includes("employees")
    ? "employee"
    : location.pathname.includes("freelancers")
      ? "freelancer"
      : "contact";

  const {
    data: person,
    isLoading,
    isError,
    error,
  } = usePerson(isNew ? undefined : id);
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  useSetCurrentEntity(
    "person",
    isNew ? undefined : id,
    person ? `${person.first_name} ${person.last_name}` : undefined,
  );
  const headerTitle = (() => {
    if (isNew) {
      if (defaultType === "employee") return "New Employee";
      if (defaultType === "freelancer") return "New Freelancer";
      return "New Person";
    }
    if (person) {
      const numberPrefix = person.number ? `${person.number} – ` : "";
      return `${numberPrefix}${person.first_name} ${person.last_name}`;
    }
    return undefined;
  })();

  usePageHeader({ title: headerTitle });

  const isEmployeePerson = person?.person_type === "employee";
  const thisYear = new Date().getFullYear();

  // Tab state
  const tabParam = searchParams.get("tab");
  const currentTab: TabValue =
    tabParam && VALID_TABS.includes(tabParam as TabValue)
      ? (tabParam as TabValue)
      : "details";

  // Year state from URL (for absences-overtime tab)
  const yearParam = searchParams.get("year");
  const selectedYear = yearParam ? parseInt(yearParam, 10) : thisYear;
  const year = isNaN(selectedYear) ? thisYear : selectedYear;

  const handleTabChange = (value: string) => {
    const params: Record<string, string> = { tab: value };
    if (year !== thisYear) params.year = String(year);
    setSearchParams(params, { replace: true });
  };
  const handleYearChange = (newYear: number) => {
    const params: Record<string, string> = { tab: "absences-overtime" };
    if (newYear !== thisYear) params.year = String(newYear);
    setSearchParams(params, { replace: true });
  };

  const getBackPath = () => {
    if (location.pathname.includes("employees")) return "/people/employees";
    if (location.pathname.includes("freelancers")) return "/people/freelancers";
    return "/people";
  };

  const handleSubmit = async (data: PersonInsert | PersonUpdate) => {
    if (isNew) {
      return await createPerson.mutateAsync(data as PersonInsert);
    } else if (id) {
      await updatePerson.mutateAsync({ id, updates: data as PersonUpdate });
      navigate(getBackPath());
    }
  };

  if (isLoading && !isNew) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!isNew && !person) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={person}
        entityName="person"
        backTo={getBackPath()}
        backLabel="Back to People"
      >
        {null}
      </QueryResult>
    );
  }

  // Simple view for new persons (no tabs)
  if (isNew) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(getBackPath())}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            <PersonForm
              person={undefined}
              onSubmit={handleSubmit}
              onCancel={() => navigate(getBackPath())}
              isLoading={createPerson.isPending || updatePerson.isPending}
              defaultType={defaultType}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tabbed view for existing persons
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(getBackPath())}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {person && (
          <PersonContactDownloadButton
            firstName={person.first_name}
            lastName={person.last_name}
            email={person.email}
            phone={person.phone}
            companyName={person.companies?.name ?? null}
            variant="outline"
            size="sm"
          />
        )}
      </div>

      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          {isEmployeePerson && (
            <TabsTrigger value="employment">Employment</TabsTrigger>
          )}
          {isEmployeePerson && (
            <TabsTrigger value="absences-overtime">
              Absences & Overtime
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <PersonForm
                person={person}
                onSubmit={handleSubmit}
                onCancel={() => navigate(getBackPath())}
                isLoading={updatePerson.isPending}
                defaultType={defaultType}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          {id && <PersonBookings personId={id} />}
        </TabsContent>

        <TabsContent value="skills" className="mt-6">
          {id && <SkillMatrix personId={id} />}
        </TabsContent>

        {isEmployeePerson && (
          <TabsContent value="employment" className="mt-6">
            <div className="space-y-6">
              <EmploymentPeriodForm personId={id!} />
              <EntitlementCard personId={id!} year={thisYear} />
            </div>
          </TabsContent>
        )}

        {isEmployeePerson && (
          <TabsContent value="absences-overtime" className="mt-6">
            <AbsencesOvertimePanel
              personId={id!}
              year={year}
              onYearChange={handleYearChange}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
